import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FaFileInvoiceDollar,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaDownload,
  FaShareSquare,
  FaEnvelope,
  FaWhatsapp,
  FaSearch,
  FaClock,
  FaCheck,
  FaTimes,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaFilter,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaThLarge,
  FaList,
  FaColumns,
  FaCheckSquare,
  FaSquare,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight
} from 'react-icons/fa';
import Modal from '../components/Modal/Modal';
import CustomSelect from '../components/CustomSelect/CustomSelect';
import { storage } from '../utils/storage';
import api from '../utils/api';
import { addNotification } from '../redux/slices/uiSlice';
import { saveColumnPreferences, updateColumnPreferences } from '../redux/slices/settingsSlice';
import { showToast } from '../utils/toast';
import useConfirm from '../hooks/useConfirm';
import ServiceTags from '../components/ServiceTags/ServiceTags';
import Highlight from '../utils/Highlight';
import { STORAGE_KEYS, APP_NAME, API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import './Quotations.scss';
import ServiceTypeInput from '../components/ServiceTypeInput/ServiceTypeInput';
import PageHero from '../components/PageHero/PageHero';
import { useLanguage } from '../context/LanguageContext';


import { formatDateDisplay, formatDateISO } from '../utils/date';
import generateQuotationId from '../utils/quotationId';
import ShareChoiceModal from '../components/ShareChoiceModal/ShareChoiceModal';
import { buildQuotationWhatsApp, buildQuotationEmail } from '../utils/shareTemplates';

// Draft key for quotations form (session-only)
const QUOTATION_DRAFT_KEY = 'photoflow_quotation_draft';
const QUOTATION_COLUMN_DEFAULTS = {
  number: true,
  id: true,
  orderType: false,
  services: true,
  amount: true,
  date: true,
  mainEventDate: true,
  status: true,
};
const normalizeOrderType = (value) => {
  const orderType = String(value || '').trim().toLowerCase();
  if (orderType === 'sub' || orderType === 'suborder' || orderType === 'sub order') return 'sub';
  return 'own';
};

const Quotations = () => {
  const { t } = useLanguage();
  // Get company and bank details from Redux settings
  const { company, bank, notifications, columnPreferences } = useSelector((state) => state.settings);
  
  // Initialize with empty array - data will be loaded from server only
  const [quotations, setQuotations] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [editingQuotationId, setEditingQuotationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrderType, setFilterOrderType] = useState('all');
  const [statFilter, setStatFilter] = useState(null);
  // Date range filter for quotations (YYYY-MM-DD)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'name', 'amount'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  // Default to grid view on mobile devices (width < 768px)
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 'grid';
    }
    return 'table';
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(QUOTATION_COLUMN_DEFAULTS);
  // Share dialog: which quotation + which channel (whatsapp | email)
  const [shareTarget, setShareTarget] = useState(null);
  const [shareChannel, setShareChannel] = useState('whatsapp');

  // Refs for column menu and toggle button to detect outside clicks
  const columnMenuRef = useRef(null);
  const columnBtnRef = useRef(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    services: [],
    items: [{ service: '', description: '', quantity: 1, price: 0 }],
    additionalCharges: [], // { description: '', amount: 0 }
    terms: [], // array of selected term strings
    showEventDateInPdf: true, // checkbox to show/hide event date in PDF
    showTermsInPdf: true, // checkbox to show/hide terms in PDF
    showDescriptionInPdf: false, // checkbox to show/hide description column in PDF
    showPaymentDetailsInPdf: true, // checkbox to show/hide payment details in PDF
    showQrCodeInPdf: true // checkbox to show/hide QR code in PDF
  });

  // available services loaded from Services page (persisted); start empty (no sample data)
  const [availableServices, setAvailableServices] = useState([]);
  
  // Track pending new services that haven't been saved to database yet
  const [pendingNewServices, setPendingNewServices] = useState([]);

  // Combine available services and pending new services for dropdown display
  const allServices = useMemo(() => {
    return [...availableServices, ...pendingNewServices];
  }, [availableServices, pendingNewServices]);
  
  // Load policies - will be fetched from database via API
  const [groupedPolicies, setGroupedPolicies] = useState({});
  const confirm = useConfirm();

  useEffect(() => {
    const savedColumns = columnPreferences?.quotations;
    if (savedColumns && typeof savedColumns === 'object' && !Array.isArray(savedColumns)) {
      setVisibleColumns({
        ...QUOTATION_COLUMN_DEFAULTS,
        ...savedColumns,
      });
    }
  }, [columnPreferences?.quotations]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      // Try local/persisted first (may be noop depending on storage implementation)
      try {
        const persisted = storage.getJSON(STORAGE_KEYS.SERVICES, null);
        if (Array.isArray(persisted) && persisted.length > 0) {
          const mapped = persisted.map(s => ({ id: s.id, name: s.name, price: Number(s.basePrice) || Number(s.price) || 0 }));
          setAvailableServices(mapped);
        }
      } catch (e) {
        // ignore and continue to try server
      }

      // Try to fetch services from server (preferred when authenticated)
      try {
        const remoteServices = await storage.syncFromServer(STORAGE_KEYS.SERVICES);
        if (mounted && Array.isArray(remoteServices) && remoteServices.length > 0) {
          const mapped = remoteServices.map(s => ({ id: s.id, name: s.name, price: Number(s.basePrice) || Number(s.price) || 0 }));
          setAvailableServices(mapped);
        }
      } catch (e) {
        // ignore - fallback to persisted or empty
      }

      // Policies: local first
      try {
        const policies = storage.getJSON(STORAGE_KEYS.POLICY, []);
        if (Array.isArray(policies) && policies.length > 0) {
          const grouped = {};
          policies.forEach(policy => {
            const group = policy.group || "Uncategorized";
            if (!grouped[group]) grouped[group] = [];
            grouped[group].push(policy);
          });
          setGroupedPolicies(grouped);
        }
      } catch (e) {
        // ignore and continue to try server
      }

      // Try to fetch policies from server
      try {
        const remotePolicies = await storage.syncFromServer(STORAGE_KEYS.POLICY);
        if (mounted && Array.isArray(remotePolicies) && remotePolicies.length > 0) {
          const grouped = {};
          remotePolicies.forEach(policy => {
            const group = policy.group || "Uncategorized";
            if (!grouped[group]) grouped[group] = [];
            grouped[group].push(policy);
          });
          setGroupedPolicies(grouped);
        }
      } catch (e) {
        // ignore
      }
    })();

    return () => { mounted = false; };
  }, []);

  // Track a new service locally (will be saved to database only when form is submitted)
  const handleNewService = async (serviceName) => {
    if (!serviceName || !serviceName.trim()) return;
    
    const trimmedName = serviceName.trim();
    
    // Check if service already exists in either available or pending lists (case-insensitive)
    const existsInAvailable = availableServices.some(s => 
      (s.name || '').toLowerCase() === trimmedName.toLowerCase()
    );
    const existsInPending = pendingNewServices.some(s => 
      (s.name || '').toLowerCase() === trimmedName.toLowerCase()
    );
    
    if (existsInAvailable || existsInPending) return;

    const newService = {
      id: `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: trimmedName,
      category: 'Uncategorized',
      description: '',
      basePrice: 0,
      deliverables: [],
      features: [],
    };

    // Add to pending services (not saved to database yet)
    setPendingNewServices(prev => [...prev, { id: newService.id, name: newService.name, price: 0 }]);
  };

  // Save a single pending service to the database
  const savePendingServiceToDatabase = async (service) => {
    try {
      const token = session.getToken();
      if (!token) return null;

      const res = await fetch(`${API_BASE_URL}/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(service),
      });

      if (res.ok) {
        const created = await res.json();
        return { id: created._id || created.id, name: created.name, price: Number(created.basePrice) || 0 };
      }
    } catch (e) {
      console.error('Failed to save new service:', e);
    }
    return null;
  };

  // Support opening a specific quotation when a notification is clicked while already on the page
  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/quotations') return;
        const state = detail.state || {};
        if (state.openCreate) {
          handleCreateQuotation();
        }
        if (state.openViewId) {
          const idToOpen = String(state.openViewId);
          const q = quotations.find(qt => [qt.id, qt._id].map(v => v ? String(v) : '').includes(idToOpen));
          if (q) {
            handleViewQuotation(q);
          } else {
            pendingOpenRef.current = idToOpen;
          }
        }
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [quotations]);

  // Close column menu when clicking outside (but ignore clicks on the toggle button)
  useEffect(() => {
    function handleOutsideClick(e) {
      if (!showColumnMenu) return;
      const target = e.target;
      if (columnMenuRef.current && columnMenuRef.current.contains(target)) return;
      if (columnBtnRef.current && columnBtnRef.current.contains(target)) return;
      setShowColumnMenu(false);
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showColumnMenu]);

  // Load quotations from server only - no localStorage sync
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = session.getToken();
        if (!token) return;
        
        const resp = await fetch(`${API_BASE_URL}/quotations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const remote = await resp.json();
          if (mounted && Array.isArray(remote)) {
            setQuotations(remote);
          }
        }
      } catch (e) {
        console.warn('Failed to load quotations', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleCreateQuotation = () => {
    const defaultData = getDefaultFormData();

    // If there's a saved draft, load that draft into the form instead of the blank form
    const draft = loadQuotationDraftFromSession(QUOTATION_DRAFT_KEY);
    if (draft && typeof draft === 'object' && draft.formData) {
      setFormData({
        ...defaultData,
        ...draft.formData,
        orderType: normalizeOrderType(draft.formData.orderType || defaultData.orderType),
      });
    } else {
      setFormData(defaultData);
    }
    setEditingQuotationId(null);
    setShowCreateModal(true);
  };

  const handleEditFromView = (quotation) => {
    // Prefill formData for editing. If items were not saved on the original quotation,
    // reconstruct items from services with default qty=1 and price from availableServices if available.
    const items = (quotation.items && quotation.items.length > 0)
      ? quotation.items
      : (quotation.services || []).map(svc => {
          const srv = availableServices.find(s => s.name === svc) || {};
          return { service: svc, description: '', quantity: 1, price: srv.price || 0 };
        });

    const defaults = getDefaultFormData();
    setFormData({
      ...defaults,
      clientName: quotation.clientName || '',
      clientEmail: quotation.clientEmail || '',
      clientPhone: quotation.clientPhone || '',
      services: quotation.services || [],
      items,
      additionalCharges: quotation.additionalCharges || [],
      terms: quotation.terms || [],
      // Preserve event date and PDF visibility when editing
      mainEventDate: quotation.mainEventDate || '',
      showEventDateInPdf: quotation.showEventDateInPdf || false,
      showTermsInPdf: quotation.showTermsInPdf !== false,
      showDescriptionInPdf: quotation.showDescriptionInPdf === true,
      showPaymentDetailsInPdf: quotation.showPaymentDetailsInPdf !== false,
      showQrCodeInPdf: quotation.showQrCodeInPdf !== false,
      orderType: normalizeOrderType(quotation.orderType),
      date: quotation.date || ''
    });
    setEditingQuotationId(quotation.id);
    setShowViewModal(false);
    setShowCreateModal(true);
  };

  const handleViewQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setShowViewModal(true);
  };

  const handleDeleteQuotation = async (id) => {
    const ok = await confirm('Are you sure you want to delete this quotation?');
    if (!ok) return;
    try {
      const token = session.getToken();
      if (token) {
        // Try to delete on server; fire-and-wait so it does not reappear on refresh
        await fetch(`${API_BASE_URL}/quotations/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (e) {
      // ignore network errors; we'll still update UI optimistically
      // eslint-disable-next-line no-console
      console.warn('quotation delete failed', e);
    }

    const next = quotations.filter(q => q.id !== id);
    setQuotations(next);
  };

  const handleWhatsApp = (quotation) => {
    // Open choice dialog: To Client (send directly) vs To Others (copy template)
    setShareTarget(quotation);
    setShareChannel('whatsapp');
  };

  const handleSendEmail = (quotation) => {
    // Open choice dialog: To Client (send directly) vs To Others (copy template)
    setShareTarget(quotation);
    setShareChannel('email');
  };

  const normalizeSharePhone = (raw) => {
    let phone = (raw || '').toString().replace(/\D/g, '');
    if (phone.length === 10) phone = '91' + phone;
    return phone;
  };

  const handleShareToClient = () => {
    if (!shareTarget) return;
    if (shareChannel === 'whatsapp') {
      const phone = normalizeSharePhone(shareTarget.clientPhone);
      if (!phone) {
        showToast('No phone number available for this client.');
        return;
      }
      const message = buildQuotationWhatsApp({ quotation: shareTarget, company, audience: 'client' });
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
    } else {
      const to = (shareTarget.clientEmail || '').trim();
      if (!to) {
        showToast('No email address available for this client.');
        return;
      }
      const { subject, body } = buildQuotationEmail({ quotation: shareTarget, company, audience: 'client' });
      window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  const handleShareOthersViaApp = () => {
    if (!shareTarget) return;
    if (shareChannel === 'whatsapp') {
      const message = buildQuotationWhatsApp({ quotation: shareTarget, company, audience: 'others' });
      // No phone => opens WhatsApp share picker so user can pick any contact
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    } else {
      const { subject, body } = buildQuotationEmail({ quotation: shareTarget, company, audience: 'others' });
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  const handleDownloadQuotation = (quotation) => {
    try {
  // Only consider items that have a service name
  const items = Array.isArray(quotation.items) ? quotation.items.filter(it => it && it.service && it.service.toString().trim() !== '') : [];
  const showDescriptionInPdf = quotation.showDescriptionInPdf === true;

      const itemsRows = items.length > 0 ? items.map(it => `
        <tr>
          <td class="cell-service">${(it.service || '')}</td>
          ${showDescriptionInPdf ? `<td class="cell-desc">${(it.description || '')}</td>` : ''}
          <td style="text-align:center"><mono>${(it.quantity || '')}</mono></td>
          <td style="text-align:right"><mono>₹${(parseFloat(it.price) || 0).toLocaleString('en-IN')}</mono></td>
          <td style="text-align:right"><mono>₹${((parseFloat(it.price) || 0) * (parseFloat(it.quantity) || 0)).toLocaleString('en-IN')}</mono></td>
        </tr>`).join('') : '';

      // Additional charges: render as two columns — description (spanning middle columns) and amount
      const charges = Array.isArray(quotation.additionalCharges) ? quotation.additionalCharges : [];
      const chargesRows = charges.length > 0 ? charges.map(ch => `
        <tr>
          <td colspan="${showDescriptionInPdf ? 4 : 3}">${(ch.description || 'Additional charge')}</td>
          <td style="text-align:right"><mono>₹${(parseFloat(ch.amount) || 0).toLocaleString('en-IN')}</mono></td>
        </tr>`).join('') : '';

  const itemsSubtotal = items.reduce((s, it) => s + ((parseFloat(it.price) || 0) * (parseFloat(it.quantity) || 0)), 0);
      const chargesTotal = charges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
      const computedTotal = itemsSubtotal + chargesTotal;

  // helper: convert number to words (Indian system)
      const numberToWords = (num) => {
        if (num === 0) return 'zero';
        const a = ['','one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
        const b = ['','','twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];

        const inWords = (n) => {
          let str = '';
          if (n >= 10000000) {
            str += inWords(Math.floor(n/10000000)) + ' crore ';
            n = n % 10000000;
          }
          if (n >= 100000) {
            str += inWords(Math.floor(n/100000)) + ' lakh ';
            n = n % 100000;
          }
          if (n >= 1000) {
            str += inWords(Math.floor(n/1000)) + ' thousand ';
            n = n % 1000;
          }
          if (n >= 100) {
            str += inWords(Math.floor(n/100)) + ' hundred ';
            n = n % 100;
          }
          if (n > 0) {
            if (n < 20) {
              str += a[n] + ' ';
            } else {
              str += b[Math.floor(n/10)] + (n%10 ? ' ' + a[n%10] : '') + ' ';
            }
          }
          return str.trim();
        };

        return inWords(num).replace(/\s+/g,' ').trim();
      };

      // prepare amount in words (rupees and paise)
      const totalInteger = Math.floor(computedTotal);
      const paise = Math.round((computedTotal - totalInteger) * 100);
      const rupeesWords = numberToWords(totalInteger) || 'zero';
      const paiseWords = paise ? numberToWords(paise) : '';
      const amountInWords = `${rupeesWords} rupees${paise ? ' and ' + paiseWords + ' paise' : ''} only`;
      const amountInWordsCap = amountInWords.charAt(0).toUpperCase() + amountInWords.slice(1);
      const companyLogoSrc = (typeof company?.logo === 'string' && company.logo.trim()) ? company.logo.trim() : '';
      const companyLogoMode = company?.logoMode === 'landscape' ? 'landscape' : 'square';
      const companyLogoWidth = companyLogoMode === 'landscape' ? '160px' : '64px';
      const companyLogoHeight = companyLogoMode === 'landscape' ? '64px' : '64px';
      const companyBlockWidth = companyLogoMode === 'landscape' ? '360px' : '260px';
      const companyBlockStyle = companyLogoSrc
        ? `width:${companyBlockWidth}; text-align:right; display:flex; flex-direction:column; align-items:flex-end; gap:6px`
        : 'text-align:right; display:flex; flex-direction:column; align-items:flex-end; gap:6px';

      const html = `<!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Quotation ${quotation.id}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500&display=swap');
          *{box-sizing:border-box} html,body{margin:0;padding:0}
          body{font-family:'Inter','Helvetica Neue',Helvetica,Arial,sans-serif;color:#0F172A;background:#fff;font-size:9.5pt;line-height:1.55;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          .pdf-shell{padding:22px 26px 18px}
          .accent-bar{height:4px;background:linear-gradient(90deg,#4F46E5 0%,#06B6D4 50%,#8B5CF6 100%);border-radius:999px;margin-bottom:20px}
          .doc-header{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding-bottom:16px;border-bottom:1px solid #E2E8F0}
          .doc-left{flex:1;min-width:0}
          .doc-eyebrow{font-size:7.5pt;letter-spacing:0.16em;font-weight:700;color:#6366F1;text-transform:uppercase;margin:0 0 6px}
          .doc-title{font-size:21pt;font-weight:800;letter-spacing:-0.03em;color:#0F172A;margin:0;line-height:1}
          .doc-subtitle{display:inline-flex;align-items:center;gap:8px;margin-top:10px;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:999px;padding:5px 10px;font-size:7.5pt;font-weight:600;color:#334155;letter-spacing:0.04em}
          .doc-subtitle b{color:#0F172A}
          .meta-grid{margin-top:14px;display:grid;grid-template-columns:1fr;gap:0}
          .meta-row{display:flex;gap:10px;align-items:baseline;padding:5px 0;border-bottom:1px dashed #F1F5F9}
          .meta-row:last-child{border-bottom:none}
          .meta-label{width:92px;flex-shrink:0;font-size:7.3pt;letter-spacing:0.1em;font-weight:700;color:#64748B;text-transform:uppercase}
          .meta-value{font-size:9.2pt;font-weight:600;color:#0F172A;word-break:break-word}
          .meta-value.muted{font-weight:500;color:#334155}
          .company-block{text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:8px;min-width:200px}
          .company-logo{width:64px;height:64px;display:block;border-radius:10px;object-fit:cover;border:1px solid #E2E8F0;box-shadow:0 1px 2px rgba(15,23,42,0.06)}
          .company-logo.landscape{width:148px;height:58px;border-radius:10px}
          .company-name{font-weight:800;font-size:11pt;letter-spacing:-0.02em;color:#0F172A;line-height:1.1}
          .company-contact{font-size:7.8pt;color:#64748B;line-height:1.6;text-align:right}
          .company-gstin{display:inline-block;margin-top:4px;background:#EEF2FF;border:1px solid #E0E7FF;color:#4338CA;font-size:7pt;font-weight:700;letter-spacing:0.06em;padding:3px 8px;border-radius:999px}
          .table-wrap{margin-top:18px;border:1px solid #E2E8F0;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(15,23,42,0.06)}
          table{width:100%;border-collapse:separate;border-spacing:0;margin:0}
          thead th{background:#0F172A;color:#fff;font-size:7.4pt;letter-spacing:0.11em;text-transform:uppercase;font-weight:700;padding:11px 12px;border:none;text-align:left;white-space:nowrap}
          thead th:first-child{border-top-left-radius:12px} thead th:last-child{border-top-right-radius:12px}
          thead th.center{text-align:center} thead th.right{text-align:right}
          tbody td{padding:10px 12px;font-size:9pt;color:#1E293B;border-bottom:1px solid #F1F5F9;vertical-align:top;line-height:1.45}
          tbody tr:last-child td{border-bottom:none} tbody tr:nth-child(even) td{background:#F8FAFC}
          tbody td mono{font-family:'JetBrains Mono',ui-monospace,monospace;font-weight:500}
          .cell-service{font-weight:600;color:#0F172A} .cell-desc{color:#475569;font-size:8.6pt}
          .row-subtotal td{background:#F1F5F9 !important;color:#0F172A !important;font-weight:700 !important;font-size:9pt;border-top:1px solid #E2E8F0}
          .row-charges-head td{background:#EEF2FF !important;color:#4338CA !important;font-weight:700 !important;font-size:7.4pt;letter-spacing:0.1em;text-transform:uppercase;padding:9px 12px !important;border-top:1px solid #E0E7FF;border-bottom:1px solid #E0E7FF}
          .row-total td{background:#0F172A !important;color:#fff !important;font-weight:800 !important;font-size:11pt;padding:13px 12px !important;letter-spacing:-0.01em}
          .amount-chip{margin:12px 0 0;width:100%;max-width:100%;box-sizing:border-box;display:flex;flex-direction:row-reverse;align-items:flex-start;gap:10px;background:#EEF2FF;border:1px solid #E0E7FF;border-right:3px solid #4F46E5;border-left:none;border-radius:10px;padding:10px 14px;text-align:right}
          .amount-chip-icon{width:26px;height:26px;flex-shrink:0;background:#4F46E5;color:#fff;border-radius:999px;display:flex;align-items:center;justify-content:center;font-size:10pt;line-height:1}
          .amount-chip-label{font-size:7pt;letter-spacing:0.1em;font-weight:700;color:#6366F1;text-transform:uppercase;margin:0 0 2px;text-align:right}
          .amount-chip-value{font-size:8.7pt;font-weight:600;color:#1E293B;line-height:1.4;font-style:italic;text-align:right}
          .amount-chip-content{flex:1;text-align:right}
          .terms-wrap{margin-top:18px;page-break-inside:avoid} .terms-card{background:#F8FAFC;border:1px solid #E2E8F0;border-left:3px solid #4F46E5;border-radius:10px;padding:14px 16px}
          .terms-title{margin:0 0 10px;font-size:9pt;font-weight:800;color:#0F172A;letter-spacing:-0.01em;display:flex;align-items:center;gap:8px}
          .terms-title:before{content:"";width:3px;height:14px;background:#4F46E5;border-radius:999px;display:inline-block}
          .terms-list{margin:0;padding-left:16px} .terms-list li{margin:6px 0;color:#334155;font-size:8.6pt;line-height:1.5} .terms-list li::marker{color:#94A3B8}
          .sign-row{margin-top:28px;display:flex;gap:28px} .sign-col{flex:1} .sign-line{height:48px;border-bottom:1.5px solid #CBD5E1;margin-bottom:10px}
          .sign-label{font-size:7.3pt;letter-spacing:0.12em;font-weight:700;color:#64748B;text-transform:uppercase;margin:0}
          .sign-name{margin-top:4px;font-size:9.2pt;font-weight:700;color:#0F172A} .sign-meta{font-size:7.8pt;color:#64748B;margin-top:2px}
          .payment-outer{margin-top:18px;page-break-inside:avoid} .payment-wrap{display:flex;gap:14px;align-items:stretch;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:12px;padding:14px}
          .payment-card{flex:1;background:#fff;border:1px solid #E2E8F0;border-radius:10px;padding:14px;box-shadow:0 1px 2px rgba(15,23,42,0.05)}
          .payment-card.qr{width:210px;flex:0 0 210px;text-align:center;display:flex;flex-direction:column;align-items:center;justify-content:center}
          .payment-card-title{margin:0 0 10px;font-size:8.5pt;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#0F172A;display:flex;align-items:center;gap:7px}
          .payment-card-title .dot{width:7px;height:7px;border-radius:999px;background:#4F46E5;display:inline-block}
          .payment-card.qr .payment-card-title{justify-content:center}
          .bank-row{display:flex;gap:8px;padding:5px 0;border-bottom:1px solid #F8FAFC;font-size:8.6pt}
          .bank-row:last-child{border-bottom:none} .bank-label{width:86px;flex-shrink:0;font-weight:700;color:#64748B;font-size:7.6pt;letter-spacing:0.06em;text-transform:uppercase;padding-top:1px}
          .bank-value{flex:1;font-weight:600;color:#0F172A;word-break:break-word} mono{font-family:'JetBrains Mono',ui-monospace,monospace;font-weight:500}
          .bank-note{margin-top:10px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:8px 10px;font-size:7.8pt;color:#92400E;line-height:1.5} .bank-note b{color:#78350F}
          .upi-id{margin:0 0 8px;background:#F8FAFC;border:1px dashed #CBD5E1;border-radius:8px;padding:7px 10px;font-size:8.4pt;font-weight:700;color:#0F172A;word-break:break-all}
          .upi-qr{width:132px;height:132px;border-radius:10px;object-fit:cover;border:1px solid #E2E8F0;display:block}
          .upi-hint{margin-top:8px;font-size:7.5pt;color:#94A3B8;letter-spacing:0.02em}
          .doc-footer{margin-top:18px;text-align:center;font-size:7pt;color:#94A3B8;letter-spacing:0.06em;border-top:1px solid #F1F5F9;padding-top:10px}
        </style>
      </head>
      <body>
        <div class="pdf-shell">
          <div class="accent-bar"></div>
          <div class="doc-header">
            <div class="doc-left">
              <p class="doc-eyebrow">Quotation</p>
              <h1 class="doc-title">${quotation.id}</h1>
              <div class="doc-subtitle">Issued &nbsp;<b>${formatDateDisplay(quotation.date) || "—"}</b></div>
              <div class="meta-grid">
                <div class="meta-row"><span class="meta-label">Client</span><span class="meta-value">${quotation.clientName || "—"}</span></div>
                <div class="meta-row"><span class="meta-label">Phone</span><span class="meta-value muted">${quotation.clientPhone || "—"}</span></div>
                <div class="meta-row"><span class="meta-label">Email</span><span class="meta-value muted">${quotation.clientEmail || "—"}</span></div>
                ${quotation.showEventDateInPdf && quotation.mainEventDate ? `<div class="meta-row"><span class="meta-label">Event Date</span><span class="meta-value muted">${formatDateDisplay(quotation.mainEventDate)}</span></div>` : ""}
              </div>
            </div>
            <div class="company-block" style="${companyBlockStyle}">
              ${companyLogoSrc ? `<img src="${companyLogoSrc}" alt="Company Logo" class="company-logo ${companyLogoMode === 'landscape' ? 'landscape' : ''}" style="border-radius:10px" />` : ''}
              <div class="company-name">${company?.name || 'Company Name'}</div>
              <div class="company-contact">
                ${company?.phone ? `<span><strong>Phone</strong> ${company.phone}</span><br/>` : ''}
                ${company?.email ? `<span><strong>Email</strong> ${company.email}</span>` : ''}
                ${company?.address ? `<br/><span>${company.address}</span>` : ''}
              </div>
              ${company?.gstin ? `<span class="company-gstin">GSTIN ${company.gstin}</span>` : ''}
            </div>
          </div>

          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  ${showDescriptionInPdf ? '<th>Description</th>' : ''}
                  <th class="center">Qty</th>
                  <th class="right">Unit</th>
                  <th class="right">Line Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsRows}
                <tr class="row-subtotal">
                  <td colspan="${showDescriptionInPdf ? 4 : 3}" style="text-align:right">Subtotal</td>
                  <td style="text-align:right"><mono>₹${itemsSubtotal.toLocaleString('en-IN')}</mono></td>
                </tr>
                ${charges.length > 0 ? `
                <tr class="row-charges-head">
                  <td colspan="${showDescriptionInPdf ? 5 : 4}">Additional Charges</td>
                </tr>
                ${chargesRows}
                ` : ''}
                <tr class="row-total">
                  <td colspan="${showDescriptionInPdf ? 4 : 3}" style="text-align:right">Total</td>
                  <td style="text-align:right"><mono>₹${computedTotal.toLocaleString('en-IN')}</mono></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="amount-chip">
            <div class="amount-chip-icon">₹</div>
            <div class="amount-chip-content">
              <div class="amount-chip-label">Amount in words</div>
              <div class="amount-chip-value">${amountInWordsCap}</div>
            </div>
          </div>
          
          ${(quotation.showTermsInPdf !== false && quotation.terms && quotation.terms.length > 0) ? `
          <div class="terms-wrap">
            <div class="terms-card">
              <h3 class="terms-title">Terms & Conditions</h3>
              <ul class="terms-list">
                ${quotation.terms.map(term => `<li>${term}</li>`).join('')}
              </ul>
            </div>
          </div>
          ` : ''}

          <!-- Signature blocks -->
          <div class="sign-row">
            <div class="sign-col">
              <div class="sign-line"></div>
              <p class="sign-label">Client Signature</p>
              <div class="sign-name">${quotation.clientSignature || ''}</div>
            </div>
            <div class="sign-col" style="text-align:right">
              <div class="sign-line"></div>
              <p class="sign-label" style="text-align:right">Authorized Signatory</p>
              <div class="sign-name">${company?.name || 'Company Name'}</div>
              ${company?.phone ? `<div class="sign-meta">Phone: ${company.phone}</div>` : ''}
              ${company?.email ? `<div class="sign-meta">Email: ${company.email}</div>` : ''}
            </div>
          </div>

          ${(quotation.showPaymentDetailsInPdf !== false || quotation.showQrCodeInPdf !== false) ? `
          <div class="payment-outer">
            <div class="payment-wrap">
            ${quotation.showPaymentDetailsInPdf !== false ? `
            <div class="payment-card">
              <h3 class="payment-card-title"><span class="dot"></span> Payment Details</h3>
              <div class="bank-grid">
                ${bank?.bankName ? `<div class="bank-row"><span class="bank-label">Bank</span><span class="bank-value">${bank.bankName}</span></div>` : ''}
                ${bank?.accountHolder ? `<div class="bank-row"><span class="bank-label">Account</span><span class="bank-value">${bank.accountHolder}</span></div>` : ''}
                ${bank?.accountNumber ? `<div class="bank-row"><span class="bank-label">A/C No.</span><span class="bank-value"><mono>${bank.accountNumber}</mono></span></div>` : ''}
                ${bank?.branch ? `<div class="bank-row"><span class="bank-label">Branch</span><span class="bank-value">${bank.branch}</span></div>` : ''}
                ${bank?.ifsc ? `<div class="bank-row"><span class="bank-label">IFSC</span><span class="bank-value"><mono>${bank.ifsc}</mono></span></div>` : ''}
                ${bank?.address ? `<div class="bank-row"><span class="bank-label">Address</span><span class="bank-value">${bank.address}</span></div>` : ''}
              </div>
              <div class="bank-note"><b>Note:</b> After the successful payment, please inform us with the Quotation ID (${quotation.id}) as the payment reference.</div>
            </div>
            ` : ''}

            ${quotation.showQrCodeInPdf !== false ? `
            <div class="payment-card qr">
              <h3 class="payment-card-title"><span class="dot" style="background:#10B981"></span> UPI Payment</h3>
              ${bank?.upiId ? `<div class="upi-id">${bank.upiId}</div>` : ''}
              ${bank?.upiQr ? `<img src="${bank.upiQr}" alt="UPI QR" class="upi-qr" />` : `<div style="width:132px;height:132px;border:1px dashed #CBD5E1;border-radius:10px;display:flex;align-items:center;justify-content:center;color:#94A3B8;font-size:7.5pt">QR not set</div>`}
              <div class="upi-hint">Scan or use the UPI ID to pay</div>
            </div>
            ` : ''}
            </div>
          </div>
          ` : ''}
          <div class="doc-footer">Thank you for your trust &nbsp;·&nbsp; This is a computer-generated document</div>
        </div>
      </body>
      </html>`;

      const win = window.open('', '_blank');
      if (!win) {
        showToast('Popup blocked. Please allow popups for this site to download the PDF.');
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
      // Give the new window a moment to render before printing
      setTimeout(() => { try { win.print(); } catch (e) { console.error(e); } }, 500);
    } catch (e) {
      console.error('download error', e);
      showToast('Unable to prepare the quotation for download.');
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    // Persist status change to server when authenticated
    try {
      const token = session.getToken();
      if (token) {
        const resp = await fetch(`${API_BASE_URL}/quotations/${encodeURIComponent(id)}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus }),
        });
        if (resp.ok) {
          // Update local state only after successful server update
          const next = quotations.map(q => q.id === id ? { ...q, status: newStatus } : q);
          setQuotations(next);
        } else {
          showToast('Failed to update status on server.');
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to persist quotation status', e);
      showToast('Failed to update status on server.');
    }
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      items: [{ service: '', description: '', quantity: 1, price: 0 }, ...formData.items]
    });
  };

  const removeLineItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateLineItem = (index, field, value) => {
    // Use functional update to avoid stale closures and append a blank row
    setFormData(prev => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      // Ensure the target index exists
      while (items.length <= index) {
        items.push({ service: '', description: '', quantity: 1, price: 0 });
      }

      // Allow temporary empty strings while the user types, but store numbers when possible
      if (field === 'quantity') {
        items[index][field] = value === '' ? '' : parseInt(value, 10) || 0;
      } else if (field === 'price') {
        items[index][field] = value === '' ? '' : parseFloat(value) || 0;
      } else {
        items[index][field] = value;
      }

      // If user filled the service field on the last row, append one empty row
      if (field === 'service') {
        const justFilled = value && value.toString().trim() !== '';
        const wasLastRow = index === items.length - 1;
        if (justFilled && wasLastRow) {
          items.push({ service: '', description: '', quantity: 1, price: 0 });
        }
      }

      return { ...prev, items };
    });
  };

  const selectService = (index, serviceName) => {
    // Find matching service from available services (if any)
    const service = availableServices.find(s => s.name === serviceName);

    // Set service name and price (use matched price if found, otherwise keep existing or default to 0)
    setFormData(prev => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      while (items.length <= index) {
        items.push({ service: '', description: '', quantity: 1, price: 0 });
      }
      const prevService = items[index].service;
      const existingPrice = items[index].price || 0;
      // Use the matched service price if found, otherwise keep existing price for custom services
      const newPrice = service ? service.price : existingPrice;
      items[index] = { ...items[index], service: serviceName, price: newPrice };

      const justFilled = serviceName && serviceName.toString().trim() !== '';
      const wasLastRow = index === items.length - 1;
      // Only append when the row was previously empty (no service) and now filled
      if (justFilled && wasLastRow && (!prevService || prevService.toString().trim() === '')) {
        items.push({ service: '', description: '', quantity: 1, price: 0 });
      }

      return { ...prev, items };
    });
  };

  const calculateSubtotal = () => {
    // Only include line items that have a non-empty service name
    return (Array.isArray(formData.items) ? formData.items : []).filter(it => it && it.service && it.service.toString().trim() !== '').reduce((sum, item) => {
      const q = parseFloat(item.quantity) || 0;
      const p = parseFloat(item.price) || 0;
      return sum + (q * p);
    }, 0);
  };
  const calculateAdditionalCharges = () => {
    return (formData.additionalCharges || []).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateAdditionalCharges();
  };

  const addAdditionalCharge = () => {
    setFormData({ ...formData, additionalCharges: [...(formData.additionalCharges || []), { description: '', amount: 0 }] });
  };

  const updateAdditionalCharge = (index, field, value) => {
    const charges = [...(formData.additionalCharges || [])];
    if (field === 'amount') {
      charges[index] = { ...charges[index], [field]: value === '' ? '' : (parseFloat(value) || 0) };
    } else {
      charges[index] = { ...charges[index], [field]: value };
    }
    setFormData({ ...formData, additionalCharges: charges });
  };

  const removeAdditionalCharge = (index) => {
    const charges = (formData.additionalCharges || []).filter((_, i) => i !== index);
    setFormData({ ...formData, additionalCharges: charges });
  };

  // Handle term selection/deselection
  const handleTermToggle = (termContent) => {
    const currentTerms = formData.terms || [];
    if (currentTerms.includes(termContent)) {
      // Remove term
      setFormData({ ...formData, terms: currentTerms.filter(t => t !== termContent) });
    } else {
      // Add term
      setFormData({ ...formData, terms: [...currentTerms, termContent] });
    }
  };

  // Select all terms in a group
  const handleSelectGroup = (groupName, selectAll) => {
    const currentTerms = formData.terms || [];
    const groupTerms = (groupedPolicies[groupName] || []).map(p => p.content);
    
    if (selectAll) {
      // Add all group terms that aren't already selected
      const newTerms = [...new Set([...currentTerms, ...groupTerms])];
      setFormData({ ...formData, terms: newTerms });
    } else {
      // Remove all group terms
      const newTerms = currentTerms.filter(t => !groupTerms.includes(t));
      setFormData({ ...formData, terms: newTerms });
    }
  };

  // Check if all terms in a group are selected
  const isGroupFullySelected = (groupName) => {
    const currentTerms = formData.terms || [];
    const groupTerms = (groupedPolicies[groupName] || []).map(p => p.content);
    return groupTerms.length > 0 && groupTerms.every(t => currentTerms.includes(t));
  };

  // Check if some (but not all) terms in a group are selected
  const isGroupPartiallySelected = (groupName) => {
    const currentTerms = formData.terms || [];
    const groupTerms = (groupedPolicies[groupName] || []).map(p => p.content);
    const selectedCount = groupTerms.filter(t => currentTerms.includes(t)).length;
    return selectedCount > 0 && selectedCount < groupTerms.length;
  };

  const dispatch = useDispatch();

  function getDefaultFormData() {
    return {
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      orderType: 'own',
      services: [],
      items: [{ service: '', description: '', quantity: 1, price: 0 }],
      additionalCharges: [],
      mainEventDate: '',
      terms: [],
      showEventDateInPdf: true,
      showTermsInPdf: true,
      showDescriptionInPdf: false,
      showPaymentDetailsInPdf: true,
      showQrCodeInPdf: true,
      date: ''
    };
  }

  // Draft helpers for quotations (component state only - no persistence)
  const makeQuotationDraftKey = (id) => (id ? `${QUOTATION_DRAFT_KEY}_${String(id)}` : QUOTATION_DRAFT_KEY);
  const saveQuotationDraftToSession = (key) => {
    const data = { formData };
    sessionStorage.setItem(key, JSON.stringify(data));
  };
  const loadQuotationDraftFromSession = (key) => {
    try {
      const data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  };
  const removeQuotationDraftFromSession = (key) => {
    sessionStorage.removeItem(key);
  };

  // Reset the create form to its initial values (does not close the modal)
  const resetCreateForm = () => {
    setFormData(getDefaultFormData());
    try { removeQuotationDraftFromSession(makeQuotationDraftKey()); } catch (e) {}
  };

  // Auto-save draft when form data changes and modal is open
  useEffect(() => {
    if (showCreateModal && formData) {
      // Check if form has any data (not empty)
      const hasData = formData.clientName || formData.clientEmail || formData.clientPhone ||
                      formData.services.length > 0 || formData.items.some(item => item.service || item.description || item.price > 0) ||
                      formData.additionalCharges.length > 0 || formData.terms.length > 0;
      if (hasData) {
        saveQuotationDraftToSession(QUOTATION_DRAFT_KEY);
      }
    }
  }, [formData, showCreateModal]);

  const handleSubmitQuotation = async () => {
    // Save any pending new services to database first
    if (pendingNewServices.length > 0) {
      const savedServices = [];
      for (const pendingService of pendingNewServices) {
        // Find the actual price from form items for this service
        const formItem = formData.items.find(item => item.service === pendingService.name);
        const actualPrice = formItem ? (parseFloat(formItem.price) || 0) : 0;
        
        const fullService = {
          id: pendingService.id,
          name: pendingService.name,
          category: 'Uncategorized',
          description: '',
          basePrice: actualPrice,
          deliverables: [],
          features: [],
        };
        const saved = await savePendingServiceToDatabase(fullService);
        if (saved) {
          savedServices.push(saved);
        }
      }
      
      // Update availableServices with newly saved services
      if (savedServices.length > 0) {
        setAvailableServices(prev => [...prev, ...savedServices]);
        // Clear pending services as they're now saved
        setPendingNewServices([]);
      }
    }

    // sanitize inputs
    const clientName = (formData.clientName || '').toString().replace(/[^A-Za-z\s]/g, '').trim();
    const clientPhone = (formData.clientPhone || '').toString().replace(/\D/g, '');
    const clientEmail = (formData.clientEmail || '').toString().trim();

    if (!clientName) {
      showToast('Please provide a valid client name (letters and spaces only).');
      return;
    }
    if (!/^\d{10}$/.test(clientPhone)) {
      showToast('Please enter a valid 10-digit phone number (numbers only).');
      return;
    }
    if (clientEmail) {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(clientEmail)) {
        showToast('Please provide a valid email address.');
        return;
      }
    }

    const payload = {
      clientName,
      clientEmail,
      clientPhone,
      // Persist only items that have a non-empty service name
      items: Array.isArray(formData.items) ? formData.items.filter(it => it && it.service && it.service.toString().trim() !== '') : [],
      services: (Array.isArray(formData.items) ? formData.items.filter(it => it && it.service && it.service.toString().trim() !== '') : []).map(item => item.service),
      additionalCharges: formData.additionalCharges || [],
  terms: formData.terms || [],
      totalAmount: calculateTotal(),
      status: editingQuotationId ? 'pending' : 'pending',
        mainEventDate: formData.mainEventDate || '',
        showEventDateInPdf: formData.showEventDateInPdf || false,
        showTermsInPdf: formData.showTermsInPdf !== false,
          showDescriptionInPdf: formData.showDescriptionInPdf === true,
        showPaymentDetailsInPdf: formData.showPaymentDetailsInPdf !== false,
        showQrCodeInPdf: formData.showQrCodeInPdf !== false,
          orderType: normalizeOrderType(formData.orderType),
  date: editingQuotationId ? formData.date || formatDateISO(new Date()) : formatDateISO(new Date()),
      ...(editingQuotationId && { id: editingQuotationId }),
    };

    try {
      const token = session.getToken();
      if (token) {
        if (editingQuotationId) {
          // update existing on server
          const resp = await fetch(`${API_BASE_URL}/quotations/${encodeURIComponent(editingQuotationId)}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (resp.ok) {
            const updated = await resp.json();
            const updatedWithDate = { ...updated, date: updated.date || payload.date || formatDateISO(new Date()) };
            const next = quotations.map(q => q.id === editingQuotationId ? updatedWithDate : q);
            setQuotations(next);
            showToast("Quotation updated successfully", { type: 'success' });
          } else {
            showToast('Failed to update quotation on server.');
          }
          setEditingQuotationId(null);
        } else {
          // create new on server
          const newPayload = { id: generateQuotationId(quotations), ...payload };
          const resp = await fetch(`${API_BASE_URL}/quotations`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(newPayload),
          });
          if (resp.ok) {
            const created = await resp.json();
            const createdWithDate = { ...created, date: created.date || payload.date || formatDateISO(new Date()) };
            const next = [createdWithDate, ...quotations];
            setQuotations(next);
            // creation toast suppressed — notification only; trigger instant server notification fetch
            if (notifications?.events) {
              try {
                dispatch(addNotification({
                  title: 'Quotation created',
                  message: `${createdWithDate.id} — ${createdWithDate.clientName || 'No client'}`,
                  type: 'info',
                  timestamp: new Date().toISOString(),
                  path: '/quotations',
                  data: { openViewId: createdWithDate.id }
                }));
              } catch (e) {}
            }
            // instant refresh: notify NotificationPanel to fetch unread count immediately
            try { window.dispatchEvent(new CustomEvent('notifications:refresh')); } catch (e) {}
            // also schedule quick follow-ups to catch DB replication lag (500ms / 1500ms)
            try { setTimeout(() => window.dispatchEvent(new CustomEvent('notifications:refresh')), 600); } catch (e) {}
            try { setTimeout(() => window.dispatchEvent(new CustomEvent('notifications:refresh')), 1600); } catch (e) {}
          } else {
            showToast('Failed to create quotation on server.');
          }
        }
      } else {
        showToast('Please login to save quotations.');
        return;
      }
    } catch (e) {
      // network error
      // eslint-disable-next-line no-console
      console.warn('persist quotation failed', e);
      showToast('Network error: Failed to save quotation to server.');
    }
    setShowCreateModal(false);
  };

  const location = useLocation();
  const pendingOpenRef = useRef(null);

  useEffect(() => {
    if (location?.state?.openCreate) {
      handleCreateQuotation();
      try { window.history.replaceState({}, document.title); } catch (e) {}
    }

    // Deep-link for push clicks when app was closed (SW opens /quotations?id=...)
    const getDeepLinkId = () => {
      if (location?.state?.openViewId) return String(location.state.openViewId);
      try {
        const params = new URLSearchParams(window.location.search);
        const qId = params.get('id');
        if (qId) return String(qId);
      } catch (e) {}
      return null;
    };
    const idToOpen = getDeepLinkId();
    if (idToOpen) {
      const q = quotations.find(qt => [qt.id, qt._id].map(v => v ? String(v) : '').includes(idToOpen));
      if (q) {
        handleViewQuotation(q);
      } else {
        pendingOpenRef.current = idToOpen;
      }
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('id')) {
          url.searchParams.delete('id');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else {
          window.history.replaceState({}, document.title);
        }
      } catch (e) {}
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle push deep-link when app is already open (SW navigates existing tab)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId && !pendingOpenRef.current) {
        const q = quotations.find(qt => [qt.id, qt._id].map(v => v ? String(v) : '').includes(String(qId)));
        if (q) {
          handleViewQuotation(q);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, document.title, url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else {
          pendingOpenRef.current = String(qId);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // If a pending open id was set (because quotations weren't loaded when navigation happened),
  // try to open it now that `quotations` may have been populated.
  useEffect(() => {
    if (pendingOpenRef.current) {
      const idToOpen = String(pendingOpenRef.current);
      const q = quotations.find(qt => [qt.id, qt._id].map(v => v ? String(v) : '').includes(idToOpen));
      if (q) {
        handleViewQuotation(q);
        pendingOpenRef.current = null;
        try { window.history.replaceState({}, document.title); } catch (e) {}
      }
    }
  }, [quotations]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const toggleColumn = (columnKey) => {
    // NOTE: never dispatch inside a setState updater — updaters must be pure.
    const next = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey],
    };
    setVisibleColumns(next);
    dispatch(updateColumnPreferences({ quotations: next }));
    dispatch(saveColumnPreferences({ quotations: next }));
  };

  const toggleAllColumns = (checked) => {
    const newVisibleColumns = {};
    columnOptions.forEach(column => {
      newVisibleColumns[column.key] = checked;
    });
    const next = { ...QUOTATION_COLUMN_DEFAULTS, ...newVisibleColumns };
    setVisibleColumns(next);
    dispatch(updateColumnPreferences({ quotations: next }));
    dispatch(saveColumnPreferences({ quotations: next }));
  };

  const isAllColumnsSelected = () => {
    return columnOptions.every(column => visibleColumns[column.key]);
  };

  const columnOptions = [
    { key: 'id', label: t('quotation.colQuotId') },
    { key: 'orderType', label: t('common.orderType') },
    { key: 'services', label: t('common.services') },
    { key: 'amount', label: t('common.amount') },
    { key: 'date', label: t('common.date') },
    { key: 'mainEventDate', label: t('quotation.colMainEventDate') },
    { key: 'status', label: t('common.status') }
  ];

  const getFilteredQuotations = (opts = {}) => {
    const skipStatFilter = opts?.skipStatFilter === true;
    const skipStatusFilter = opts?.skipStatusFilter === true;
    const q = (searchQuery || '').toString().trim().toLowerCase();
    let filtered = quotations.filter(quotation => {
      // Date range filtering
      if (fromDate || toDate) {
        const qDate = quotation.date ? new Date(quotation.date) : null;
        if (!qDate) return false;
        if (fromDate) {
          const f = new Date(fromDate); f.setHours(0,0,0,0);
          if (qDate < f) return false;
        }
        if (toDate) {
          const t = new Date(toDate); t.setHours(23,59,59,999);
          if (qDate > t) return false;
        }
      }

      const clientName = (quotation?.clientName || '').toString().toLowerCase();
      const id = (quotation?.id || '').toString().toLowerCase();
      const rawDate = (quotation?.date || '').toString().toLowerCase();
      const formattedDate = quotation.date ? formatDateDisplay(quotation.date).toString().toLowerCase() : '';
      const rawMainEventDate = (quotation?.mainEventDate || '').toString().toLowerCase();
      const formattedMainEventDate = quotation.mainEventDate ? formatDateDisplay(quotation.mainEventDate).toString().toLowerCase() : '';

      // Services: array of strings
      const servicesArr = Array.isArray(quotation.services) ? quotation.services : [];
      const servicesText = servicesArr.join(' ').toLowerCase();

      // Amount matching: allow queries like "5000", "₹5,000" or partial digits
      const rawAmount = quotation.totalAmount;
      const amountNum = (rawAmount === undefined || rawAmount === null) ? NaN : Number(rawAmount);

      let matchesSearch = false;
      if (!q) {
        matchesSearch = true;
      } else {
        matchesSearch = (
          clientName.includes(q) ||
          id.includes(q) ||
          rawDate.includes(q) ||
          formattedDate.includes(q) ||
          rawMainEventDate.includes(q) ||
          formattedMainEventDate.includes(q) ||
          servicesText.includes(q)
        );

        if (!matchesSearch) {
          // numeric query handling
          const numericQuery = q.replace(/[^0-9.]/g, '');
          if (numericQuery) {
            const qNum = parseFloat(numericQuery);
            if (!Number.isNaN(qNum) && !Number.isNaN(amountNum)) {
              if (Math.abs(amountNum - qNum) < 0.001) {
                matchesSearch = true;
              } else if (String(amountNum).toLowerCase().includes(numericQuery)) {
                matchesSearch = true;
              }
            }
          }
        }
      }

      const matchesStatus = skipStatusFilter || filterStatus === 'all' || quotation.status === filterStatus;
      const matchesOrderType =
        filterOrderType === 'all' || normalizeOrderType(quotation.orderType) === filterOrderType;
      const matchesStat = skipStatFilter || !statFilter || quotation.status === statFilter;

      return matchesSearch && matchesStatus && matchesOrderType && matchesStat;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.clientName.localeCompare(b.clientName);
          break;
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'mainEventDate':
          comparison = new Date(a.mainEventDate || 0) - new Date(b.mainEventDate || 0);
          break;
        case 'amount':
          comparison = a.totalAmount - b.totalAmount;
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#fbbf24';
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <FaClock />;
      case 'approved': return <FaCheck />;
      case 'rejected': return <FaTimes />;
      default: return <FaClock />;
    }
  };

  const filteredQuotations = getFilteredQuotations();
  // Stats ignore both dropdown + card status filters so selecting a card
  // never changes the other stat counts (still respects search/order/date).
  const quotationsForStats = getFilteredQuotations({ skipStatFilter: true, skipStatusFilter: true });

  // Pagination logic
  const totalPages = Math.ceil(filteredQuotations.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedQuotations = filteredQuotations.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, statFilter, filterOrderType, fromDate, toDate, sortBy, sortOrder]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const totalValue = quotationsForStats
    .reduce((sum, q) => sum + (parseFloat(q.totalAmount) || 0), 0);

  const pendingValue = quotationsForStats
    .filter(q => q.status === 'pending')
    .reduce((sum, q) => sum + (parseFloat(q.totalAmount) || 0), 0);

  const approvedValue = quotationsForStats
    .filter(q => q.status === 'approved')
    .reduce((sum, q) => sum + (parseFloat(q.totalAmount) || 0), 0);

  // Rejected stats
  const rejectedCount = quotationsForStats.filter(q => q.status === 'rejected').length;
  const rejectedValue = quotationsForStats
    .filter(q => q.status === 'rejected')
    .reduce((sum, q) => sum + (parseFloat(q.totalAmount) || 0), 0);

  // Keep dropdown filter and stat cards directly proportional (bidirectional sync).
  // 'all' <-> Total (statFilter null); otherwise both hold the same status value.
  const toggleStatFilter = (status) => {
    setStatFilter((prev) => {
      const next = prev === status ? null : status;
      setFilterStatus(next === null ? 'all' : next);
      return next;
    });
  };

  const resetStatFilter = () => {
    setStatFilter(null);
    setFilterStatus('all');
  };

  const handleFilterStatusChange = (status) => {
    setFilterStatus(status);
    setStatFilter(status === 'all' ? null : status);
  };

  const handleExportQuotations = async () => {
    try {
      const confirmed = await confirm('Export quotations to CSV?', {
        title: 'Export Quotations',
        confirmText: 'Export',
        cancelText: 'Cancel',
        showWarning: false
      });
      if (!confirmed) return;
      const rows = (Array.isArray(filteredQuotations) && filteredQuotations.length > 0) ? filteredQuotations : quotations;
      if (!rows || rows.length === 0) { showToast('No quotations to export'); return; }

      // Build header according to what is visible in the table
      const headers = ['S/N', 'Client Name', 'Client Phone', 'Client Email'];
      columnOptions.forEach(col => {
        if (visibleColumns[col.key]) headers.push(col.label);
      });

      const esc = (v) => {
        const s = v === undefined || v === null ? '' : String(v);
        return '"' + s.replace(/"/g, '""') + '"';
      };

      const lines = rows.map((q, idx) => {
        const base = [idx + 1, q.clientName || '', q.clientPhone || '', q.clientEmail || ''];
        columnOptions.forEach(col => {
          if (!visibleColumns[col.key]) return;
          switch (col.key) {
            case 'id':
              base.push(q.id || '');
              break;
            case 'orderType':
              base.push(normalizeOrderType(q.orderType) === 'sub' ? 'Sub Order' : 'Own Order');
              break;
            case 'services':
              base.push(Array.isArray(q.services) ? q.services.join('; ') : (q.services || ''));
              break;
            case 'amount':
              base.push(parseFloat(q.totalAmount) || 0);
              break;
            case 'date':
              base.push(formatDateDisplay(q.date) || '');
              break;
            case 'mainEventDate':
              base.push(formatDateDisplay(q.mainEventDate) || '');
              break;
            case 'status':
              base.push(q.status || '');
              break;
            default:
              base.push('');
          }
        });
        return base.map(esc).join(',');
      });

      const csv = [headers.map(esc).join(','), ...lines].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotations_export_${formatDateISO(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { console.error('export error', e); showToast('Unable to export quotations'); }
  };

  return (
    <div className="quotations">
      {/* Header */}
      <PageHero
        icon={<FaFileInvoiceDollar />}
        title={t('pages.quotations')}
        subtitle={t('pages.quotationsSubtitle')}
        actions={
          <>
            <button
              className="page-hero__btn"
              onClick={handleExportQuotations}
              title="Export Quotations"
            >
              <FaShareSquare /> {t('common.export')}
            </button>

            <button className="page-hero__btn" onClick={handleCreateQuotation}>
              <FaPlus />
              {t('quotation.newQuotation')}
            </button>
          </>
        }
      />

      {/* Stats */}
      <div className="quotations__stats">
        <div className={`quotations__stat-card${!statFilter ? ' quotations__stat-card--active' : ''}`} onClick={() => resetStatFilter()} style={{ cursor: 'pointer' }}>
          <div className="quotations__stat-icon quotations__stat-icon--blue">
            <FaFileInvoiceDollar />
          </div>
          <div className="quotations__stat-info">
            <h3>{quotationsForStats.length}</h3>
            <p>{t('quotation.statTotal')}</p>
          </div>
        </div>
        <div className={`quotations__stat-card${!statFilter ? ' quotations__stat-card--active' : ''}`} onClick={() => resetStatFilter()} style={{ cursor: 'pointer' }}>
          <div className="quotations__stat-icon quotations__stat-icon--teal">
            <FaMoneyBillWave />
          </div>
          <div className="quotations__stat-info">
            <h3>₹{totalValue.toLocaleString('en-IN')}</h3>
            <p>{t('quotation.statTotalValue')}</p>
          </div>
        </div>
        <div className={`quotations__stat-card${statFilter === 'rejected' ? ' quotations__stat-card--active' : ''}`} onClick={() => toggleStatFilter('rejected')} style={{ cursor: 'pointer' }}>
          <div className="quotations__stat-icon quotations__stat-icon--red">
            <FaTimes />
          </div>
          <div className="quotations__stat-info">
            <h3>{rejectedCount}</h3>
            <p>{t('quotation.statRejected')}</p>
          </div>
        </div>
        <div className={`quotations__stat-card${statFilter === 'rejected' ? ' quotations__stat-card--active' : ''}`} onClick={() => toggleStatFilter('rejected')} style={{ cursor: 'pointer' }}>
          <div className="quotations__stat-icon quotations__stat-icon--red-light">
            <FaMoneyBillWave />
          </div>
          <div className="quotations__stat-info">
            <h3>₹{rejectedValue.toLocaleString('en-IN')}</h3>
            <p>{t('quotation.statRejValue')}</p>
          </div>
        </div>
        <div className={`quotations__stat-card${statFilter === 'pending' ? ' quotations__stat-card--active' : ''}`} onClick={() => toggleStatFilter('pending')} style={{ cursor: 'pointer' }}>
          <div className="quotations__stat-icon quotations__stat-icon--amber">
            <FaClock />
          </div>
          <div className="quotations__stat-info">
            <h3>{quotationsForStats.filter(q => q.status === 'pending').length}</h3>
            <p>{t('common.pending')}</p>
          </div>
        </div>
        <div className={`quotations__stat-card${statFilter === 'pending' ? ' quotations__stat-card--active' : ''}`} onClick={() => toggleStatFilter('pending')} style={{ cursor: 'pointer' }}>
          <div className="quotations__stat-icon quotations__stat-icon--orange">
            <FaMoneyBillWave />
          </div>
          <div className="quotations__stat-info">
            <h3>₹{pendingValue.toLocaleString('en-IN')}</h3>
            <p>{t('quotation.statPendValue')}</p>
          </div>
        </div>
        <div className={`quotations__stat-card${statFilter === 'approved' ? ' quotations__stat-card--active' : ''}`} onClick={() => toggleStatFilter('approved')} style={{ cursor: 'pointer' }}>
          <div className="quotations__stat-icon quotations__stat-icon--green">
            <FaCheck />
          </div>
          <div className="quotations__stat-info">
            <h3>{quotationsForStats.filter(q => q.status === 'approved').length}</h3>
            <p>{t('quotation.statApproved')}</p>
          </div>
        </div>
        <div className={`quotations__stat-card${statFilter === 'approved' ? ' quotations__stat-card--active' : ''}`} onClick={() => toggleStatFilter('approved')} style={{ cursor: 'pointer' }}>
          <div className="quotations__stat-icon quotations__stat-icon--green-light">
            <FaMoneyBillWave />
          </div>
          <div className="quotations__stat-info">
            <h3>₹{approvedValue.toLocaleString('en-IN')}</h3>
            <p>{t('quotation.statAppValue')}</p>
          </div>
        </div>
        
      </div>

      {/* Toolbar */}
      <div className="quotations__toolbar">
        <div className="quotations__search">
          <FaSearch />
          <input
            type="text"
            placeholder={t('quotation.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="quotations__toolbar-right">
          <div className="quotations__date-range">
            <label>{t('common.from')}</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <label>{t('common.toDate')}</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="quotations__filter">
            <FaFilter />
            <CustomSelect
              value={filterStatus}
              onChange={handleFilterStatusChange}
              options={[
                { value: 'all', label: t('common.allStatus') },
                { value: 'pending', label: t('common.pending') },
                { value: 'approved', label: t('quotation.statusApproved') },
                { value: 'rejected', label: t('quotation.statusRejected') }
              ]}
            />
          </div>
          <div className="quotations__filter">
            <FaFilter />
            <CustomSelect
              value={filterOrderType}
              onChange={setFilterOrderType}
              options={[
                { value: 'all', label: t('common.allOrders') },
                { value: 'own', label: t('common.own') },
                { value: 'sub', label: t('common.sub') }
              ]}
            />
          </div>
          {viewMode === 'table' && (
            <div className="quotations__column-selector">
              <button
                ref={columnBtnRef}
                className="quotations__column-btn"
                onClick={() => setShowColumnMenu(!showColumnMenu)}
                title="Column Preferences"
              >
                <FaColumns />
              </button>
              {showColumnMenu && (
                <div ref={columnMenuRef} className="quotations__column-menu">
                  <div className="quotations__column-menu-header">
                    <span>{t('common.showColumns')}</span>
                    <button onClick={() => setShowColumnMenu(false)}>
                      <FaTimes />
                    </button>
                  </div>
                  <div className="quotations__column-menu-items">
                    <label className="quotations__column-menu-item quotations__column-menu-item--select-all">
                      <input
                        type="checkbox"
                        checked={isAllColumnsSelected()}
                        onChange={(e) => toggleAllColumns(e.target.checked)}
                      />
                      {isAllColumnsSelected() ? <FaCheckSquare /> : <FaSquare />}
                      <span><strong>{t('common.selectAll')}</strong></span>
                    </label>
                    <div className="quotations__column-menu-divider"></div>
                    {columnOptions.map(column => (
                      <label key={column.key} className="quotations__column-menu-item">
                        <input
                          type="checkbox"
                          checked={visibleColumns[column.key]}
                          onChange={() => toggleColumn(column.key)}
                        />
                        {visibleColumns[column.key] ? <FaCheckSquare /> : <FaSquare />}
                        <span>{column.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="quotations__view-toggle">
            <button
              className={`quotations__view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <FaList />
            </button>
            <button
              className={`quotations__view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <FaThLarge />
            </button>
          </div>
        </div>
      </div>

      {/* Quotations Table */}
      <div className="quotations__content">
        {filteredQuotations.length === 0 ? (
          <div className="quotations__empty">
            <FaFileInvoiceDollar />
            <h3>{t('quotation.emptyTitle')}</h3>
            <p>{t('quotation.emptyHint')}</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="quotations__table-container">
            <table className="quotations__table">
              <thead>
                <tr>
                  <th className="quotations__col-no" style={{ width: 60 }}>{t('common.noCol')}</th>
                  {visibleColumns.id && <th className="quotations__col-id">{t('quotation.colQuotId')}</th>}
                  {visibleColumns.orderType && <th className="quotations__col-order-type">{t('common.orderType')}</th>}
                  <th 
                    className="quotations__col-client quotations__sortable-header quotations__clickable"
                    onClick={() => handleSort('name')}
                  >
                    {t('common.client')} {' '}
                    {sortBy === 'name' ? (
                      sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
            ) : (
            <FaSort className="quotations__sort-icon-muted" />
            )}
                  </th>
                  {visibleColumns.mainEventDate && (
                    <th
                      className="quotations__col-event-date quotations__sortable-header"
                      onClick={() => handleSort('mainEventDate')}
                      style={{ cursor: 'pointer' }}
                    >
                      {t('quotation.colEventDate')} {' '}
                      {sortBy === 'mainEventDate' ? (
                        sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
                      ) : (
                        <FaSort className="quotations__sort-icon-muted" />
                      )}
                    </th>
                  )}
                  {visibleColumns.services && <th className="quotations__col-services">{t('common.services')}</th>}
                  {visibleColumns.amount && (
                    <th 
                      className="quotations__col-amount quotations__sortable-header quotations__clickable"
                      onClick={() => handleSort('amount')}
                    >
                      {t('common.amount')} {' '}
                      {sortBy === 'amount' ? (
                        sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
                      ) : (
                        <FaSort className="quotations__sort-icon-muted" />
                      )}
                    </th>
                  )}
                  {visibleColumns.date && (
                    <th 
                      className="quotations__col-date quotations__sortable-header"
                      onClick={() => handleSort('date')}
                      style={{ cursor: 'pointer' }}
                    >
                      {t('common.date')} {' '}
                      {sortBy === 'date' ? (
                        sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />
                      ) : (
                        <FaSort className="quotations__sort-icon-muted" />
                      )}
                    </th>
                  )}
                  
                  {visibleColumns.status && <th className="quotations__col-status">{t('common.status')}</th>}
                  <th className="quotations__col-actions">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedQuotations.map((quotation, idx) => (
                  <tr
                    key={quotation.id || quotation._id || `q-${idx}`}
                    onClick={() => handleViewQuotation(quotation)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleViewQuotation(quotation); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="quotations__col-no">
                      <span className="quotations__number">{idx + 1}</span>
                    </td>
                    {visibleColumns.id && (
                      <td className="quotations__col-id">
                        <span className="quotations__id"><Highlight text={quotation.id} query={searchQuery} /></span>
                      </td>
                    )}
                    {visibleColumns.orderType && (
                      <td className="quotations__col-order-type">
                        <span className={`quotations__order-type-badge quotations__order-type-badge--${normalizeOrderType(quotation.orderType)}`}>
                          {normalizeOrderType(quotation.orderType) === 'sub' ? 'Sub' : 'Own'}
                        </span>
                      </td>
                    )}
                    <td className="quotations__col-client">
                      <div className="quotations__client">
                          <div className="quotations__client-info">
                            <div className="quotations__client-name"><Highlight text={quotation.clientName} query={searchQuery} /></div>
                            <div className="quotations__client-phone"><Highlight text={quotation.clientPhone} query={searchQuery} /></div>
                            <div className="quotations__client-email"><Highlight text={quotation.clientEmail} query={searchQuery} /></div>
                          </div>
                      </div>
                    </td>
                    {visibleColumns.mainEventDate && (
                      <td className="quotations__col-event-date">
                        <span className="quotations__date">
                          <Highlight text={quotation.mainEventDate ? formatDateDisplay(quotation.mainEventDate) : ''} query={searchQuery} />
                        </span>
                      </td>
                    )}
                    {visibleColumns.services && (
                      <td className="quotations__col-services">
                        <ServiceTags
                          services={quotation.services || []}
                          containerClass="quotations__services"
                          tagClass="quotations__service-tag"
                          moreClass="quotations__service-more"
                          maxVisible={4}
                        />
                      </td>
                    )}
                    {visibleColumns.amount && (
                      <td className="quotations__col-amount">
                        <span className="quotations__amount">
                          <Highlight text={`₹${quotation.totalAmount.toLocaleString('en-IN')}`} query={searchQuery} />
                        </span>
                      </td>
                    )}
                    {visibleColumns.date && (
                      <td className="quotations__col-date">
                        <span className="quotations__date">
                          <Highlight text={formatDateDisplay(quotation.date)} query={searchQuery} />
                        </span>
                      </td>
                    )}
                    
                    
                    {visibleColumns.status && (
                      <td className="quotations__col-status">
                        <span 
                          className={`quotations__status quotations__status--${quotation.status} quotations__status--badge`}
                        >
                          {getStatusIcon(quotation.status)}
                          {quotation.status.charAt(0).toUpperCase() + quotation.status.slice(1)}
                        </span>
                      </td>
                    )}
                    <td className="quotations__col-actions">
                      <div className="quotations__actions">
                        <button
                          className="quotations__action-btn quotations__action-btn--view"
                          onClick={(e) => { e.stopPropagation(); handleViewQuotation(quotation); }}
                          title="View"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="quotations__action-btn quotations__action-btn--download"
                          title="Download PDF"
                          onClick={(e) => { e.stopPropagation(); handleDownloadQuotation(quotation); }}
                        >
                          <FaDownload />
                        </button>
                        <button
                          className="quotations__action-btn quotations__action-btn--whatsapp"
                          title="Send WhatsApp"
                          onClick={(e) => { e.stopPropagation(); handleWhatsApp(quotation); }}
                        >
                          <FaWhatsapp />
                        </button>
                        <button
                          className="quotations__action-btn quotations__action-btn--email"
                          title="Send Email"
                          onClick={(e) => { e.stopPropagation(); handleSendEmail(quotation); }}
                        >
                          <FaEnvelope />
                        </button>
                        <button
                          className="quotations__action-btn quotations__action-btn--delete"
                          onClick={(e) => { e.stopPropagation(); handleDeleteQuotation(quotation.id); }}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="quotations__grid">
            {paginatedQuotations.map((quotation, idx) => (
              <div key={quotation.id || quotation._id || `q-${idx}`} className="quotations__card">
                <div className="quotations__card-header">
                  <div className="quotations__card-header-row quotations__card-header-row--top">
                    <span className="quotations__card-no">#{idx + 1}</span>
                    <span className="quotations__card-id">{quotation.id}</span>
                  </div>
                  <div className="quotations__card-header-row quotations__card-header-row--bottom">
                    <span className={`quotations__order-type-badge quotations__order-type-badge--${normalizeOrderType(quotation.orderType)}`}>
                      {normalizeOrderType(quotation.orderType) === 'sub' ? 'Sub' : 'Own'}
                    </span>
                    <span 
                      className={`quotations__status quotations__status--${quotation.status} quotations__status--badge`}
                    >
                      {getStatusIcon(quotation.status)}
                      {quotation.status.charAt(0).toUpperCase() + quotation.status.slice(1)}
                    </span>
                  </div>
                </div>
                
                <div className="quotations__card-client">
                  <div className="quotations__card-avatar">
                    {quotation.clientName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="quotations__card-client-info">
                    <h3>{quotation.clientName}</h3>
                    <p>{quotation.clientPhone}</p>
                    <p>{quotation.clientEmail}</p>
                  </div>
                  <div className="quotations__card-event-date">
                    <label>{t('quotation.colEventDate')}</label>
                    <span><Highlight text={quotation.mainEventDate ? formatDateDisplay(quotation.mainEventDate) : 'N/A'} query={searchQuery} /></span>
                  </div>
                </div>

                <ServiceTags
                  services={quotation.services || []}
                  containerClass="quotations__card-services"
                  tagClass="quotations__service-tag"
                  moreClass="quotations__service-more"
                  maxRows={2}
                  highlightQuery={searchQuery}
                />

                <div className="quotations__card-details">
                  <div className="quotations__card-detail">
                    <FaMoneyBillWave />
                    <div>
                      <label>{t('common.amount')}</label>
                      <span><Highlight text={`₹${quotation.totalAmount.toLocaleString('en-IN')}`} query={searchQuery} /></span>
                    </div>
                  </div>
                  <div className="quotations__card-detail">
                    <FaCalendarAlt />
                    <div>
                      <label>{t('common.date')}</label>
                      <span><Highlight text={formatDateDisplay(quotation.date)} query={searchQuery} /></span>
                    </div>
                  </div>
                  
                </div>

                <div className="quotations__card-actions">
                  <button
                    className="quotations__action-btn quotations__action-btn--view"
                    onClick={() => handleViewQuotation(quotation)}
                    title="View"
                  >
                    <FaEye />
                  </button>
                  <button
                    className="quotations__action-btn quotations__action-btn--download"
                    title="Download PDF"
                    onClick={() => handleDownloadQuotation(quotation)}
                  >
                    <FaDownload />
                  </button>
                  <button
                    className="quotations__action-btn quotations__action-btn--whatsapp"
                    title="Send WhatsApp"
                    onClick={() => handleWhatsApp(quotation)}
                  >
                    <FaWhatsapp />
                  </button>
                  <button
                    className="quotations__action-btn quotations__action-btn--email"
                    title="Send Email"
                    onClick={() => handleSendEmail(quotation)}
                  >
                    <FaEnvelope />
                  </button>
                  <button
                    className="quotations__action-btn quotations__action-btn--delete"
                    onClick={() => handleDeleteQuotation(quotation.id)}
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredQuotations.length > 0 && (
        <div className="quotations__pagination">
          <div className="quotations__pagination-info">
            {t('common.showing')} {startIndex + 1} {t('common.to')} {Math.min(endIndex, filteredQuotations.length)} {t('common.of')} {filteredQuotations.length} {t('quotation.quotationsLabel')}
          </div>
          <div className="quotations__pagination-controls">
            <div className="quotations__pagination-rows">
              <label>{t('common.rowsPerPage')}</label>
              <CustomSelect
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                options={[
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                ]}
              />
            </div>
            <div className="quotations__pagination-buttons">
              <button
                className="quotations__pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First Page"
              >
                <FaAngleDoubleLeft />
              </button>
              <button
                className="quotations__pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous Page"
              >
                <FaAngleLeft />
              </button>
              <div className="quotations__pagination-pages">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                  
                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }

                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        className="quotations__pagination-btn"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1" className="quotations__pagination-ellipsis">...</span>);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`quotations__pagination-btn ${currentPage === i ? 'active' : ''}`}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<span key="ellipsis2" className="quotations__pagination-ellipsis">...</span>);
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        className="quotations__pagination-btn"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>
              <button
                className="quotations__pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next Page"
              >
                <FaAngleRight />
              </button>
              <button
                className="quotations__pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last Page"
              >
                <FaAngleDoubleRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Quotation Modal */}
      <Modal
        isOpen={showCreateModal}
        contentClassName="quotations__modal"
        title={editingQuotationId ? "Edit Quotation" : "Create Quotation"}
        headerActions={
          <div className="quotations__header-actions-group">
            <div className="quotations__order-type-toggle">
              <span className={`quotations__order-label${formData.orderType !== 'sub' ? ' quotations__order-label--active' : ''}`}>Own</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={formData.orderType === 'sub'}
                  onChange={() => setFormData({ ...formData, orderType: formData.orderType === 'sub' ? 'own' : 'sub' })}
                />
                <span className="slider" />
              </label>
              <span className={`quotations__order-label${formData.orderType === 'sub' ? ' quotations__order-label--active' : ''}`}>Sub</span>
            </div>
            {!editingQuotationId && (
              <button className="btn-secondary modal__reset" onClick={resetCreateForm} type="button">
                Reset
              </button>
            )}
          </div>
        }
        onClose={() => { saveQuotationDraftToSession(QUOTATION_DRAFT_KEY); setShowCreateModal(false); }}
        size="large"
      >
          <div className="quotations__form">
                <div className="quotations__form-section">
              <div className="quotations__form-row quotations__form-row--2col">
                <div className="quotations__form-group">
                  <label>Client Name *</label>
                  <input
                    type="text"
                    placeholder="Enter client name"
                    value={formData.clientName}
                    onChange={(e) => {
                      const filtered = (e.target.value || '').replace(/[^A-Za-z\s]/g, '');
                      setFormData({ ...formData, clientName: filtered });
                    }}
                    pattern="[A-Za-z ]+"
                  />
                </div>
                <div className="quotations__form-group">
                  <label>Client Phone *</label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    title="Phone: 10 digits, numbers only"
                    value={formData.clientPhone}
                    onChange={(e) => {
                      const digits = (e.target.value || '').toString().replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, clientPhone: digits });
                    }}
                  />
                </div>
              </div>
              <div className="quotations__form-row quotations__form-row--2col quotations__form-row--mt">
                <div className="quotations__form-group">
                  <label>{t('quotation.labelClientEmail')}</label>
                  <input
                    type="email"
                    placeholder="client@email.com"
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({ ...formData, clientEmail: (e.target.value || '').trim() })}
                  />
                </div>
                <div className="quotations__form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>{t('quotation.labelEventDate')}</label>
                    <label className="quotations__toggle-option quotations__toggle-option--inline">
                      <span className="switch switch--small">
                        <input
                          type="checkbox"
                          checked={formData.showEventDateInPdf || false}
                          onChange={(e) => setFormData({ ...formData, showEventDateInPdf: e.target.checked })}
                        />
                        <span className="slider" />
                      </span>
                      <span>{t('common.showInPdf')}</span>
                    </label>
                  </div>
                  <input
                    type="date"
                    value={formData.mainEventDate}
                    onChange={(e) => setFormData({ ...formData, mainEventDate: (e.target.value || '') })}
                  />
                </div>
              </div>
              </div>

            <div className="quotations__form-section">
              <div className="quotations__form-section-header">
                <h3>{t('common.services')}</h3>
                <button className="btn-secondary quotations__add-item-btn" onClick={addLineItem}>
                  <FaPlus /> {t('common.addItem')}
                </button>
              </div>

              {formData.items.map((item, index) => (
                <div key={index} className="quotations__line-item">
                  {/* Remove button - positioned in top right corner */}
                  {formData.items.length > 1 && (
                    <button
                      className="quotations__remove-item"
                      onClick={() => removeLineItem(index)}
                      title="Remove item"
                    >
                      <FaTimes />
                    </button>
                  )}

                  {/* Service field - full width row */}
                  <div className="quotations__line-item-row quotations__line-item-row--service">
                    <div className="quotations__form-group quotations__form-group--wide">
                      <label>{t('quotation.colService')}</label>
                      <ServiceTypeInput
                        value={item.service || ''}
                        onChange={(val) => selectService(index, val)}
                        availableServices={allServices}
                        onNewService={handleNewService}
                      />
                    </div>
                  </div>

                  {/* Quantity, Price, Total row */}
                  <div className="quotations__line-item-row quotations__line-item-row--details">
                    {/* Only show quantity/price/total when service is provided */}
                    {item.service && item.service.toString().trim() !== '' ? (
                      <>
                        <div className="quotations__form-group">
                          <label>{t('quotation.colQty')}</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                          />
                        </div>
                        <div className="quotations__form-group">
                          <label>{t('quotation.colPrice')}</label>
                          <input
                            type="number"
                            min="0"
                            value={item.price}
                            onChange={(e) => updateLineItem(index, 'price', e.target.value)}
                          />
                        </div>
                        <div className="quotations__form-group">
                          <label>{t('quotation.colTotal')}</label>
                          <div className="quotations__line-total">
                            ₹{((parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)).toLocaleString('en-IN')}
                          </div>
                        </div>
                      </>
                    ) : (
                      // keep layout consistent: show empty placeholders or small info when no service
                      <>
                        <div className="quotations__form-group quotations__form-group--placeholder">
                          <label>{t('quotation.colQty')}</label>
                          <div className="quotations__line-total">—</div>
                        </div>
                        <div className="quotations__form-group quotations__form-group--placeholder">
                          <label>{t('quotation.colPrice')}</label>
                          <div className="quotations__line-total">—</div>
                        </div>
                        <div className="quotations__form-group quotations__form-group--placeholder">
                          <label>{t('quotation.colTotal')}</label>
                          <div className="quotations__line-total">—</div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Description - always shown below */}
                  <div className="quotations__form-group">
                    <label>{t('quotation.colDescOpt')}</label>
                    <input
                      type="text"
                      placeholder="Additional details about this service"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    />
                  </div>
                </div>
              ))}

              <div className="quotations__summary">
                <div className="quotations__summary-row">
                  <span>{t('common.subtotal')}</span>
                  <span>₹{calculateSubtotal().toLocaleString('en-IN')}</span>
                </div>
                {/* Additional Charges list (editable in create modal) */}
                {(formData.additionalCharges || []).length > 0 && (
                  <div className="quotations__additional-charges">
                    {(formData.additionalCharges || []).map((c, i) => (
                      <div key={i} className="quotations__charge-row">
                        <div className="quotations__form-group quotations__charge-desc quotations__charge-desc--flex1">
                          <label>
                            {t('common.charge')}
                            <button
                              type="button"
                              onClick={() => removeAdditionalCharge(i)}
                              className="quotations__remove-item quotations__charge-remove"
                              aria-label="Remove charge"
                            >
                              <FaTimes />
                            </button>
                          </label>
                          <input
                            type="text"
                            placeholder="Charge description"
                            value={c.description}
                            onChange={(e) => updateAdditionalCharge(i, 'description', e.target.value)}
                          />
                        </div>

                        <div className="quotations__form-group quotations__charge-amount quotations__charge-amount--fixed">
                          <label>{t('common.amount')}</label>
                          <input
                            type="number"
                            min="0"
                            value={c.amount}
                            onChange={(e) => updateAdditionalCharge(i, 'amount', e.target.value)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="quotations__add-charge-row">
                  <button type="button" onClick={addAdditionalCharge} className="btn-secondary">{t('common.addCharge')}</button>
                </div>

                <div className="quotations__summary-row quotations__summary-total">
                  <span>{t('common.total')}</span>
                  <span>₹{calculateTotal().toLocaleString('en-IN')}</span>
                </div>

                {/* Terms and Conditions - Grouped by Policy */}
                <div className="quotations__form-group quotations__terms-section" style={{ marginTop: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                      Terms & Conditions
                    </label>
                    <label className="quotations__toggle-option quotations__toggle-option--inline">
                      <span className="switch switch--small">
                        <input
                          type="checkbox"
                          checked={formData.showTermsInPdf !== false}
                          onChange={(e) => setFormData({ ...formData, showTermsInPdf: e.target.checked })}
                        />
                        <span className="slider" />
                      </span>
                      <span>{t('common.showInPdf')}</span>
                    </label>
                  </div>
                  
                  {Object.keys(groupedPolicies).length === 0 ? (
                    <div style={{ 
                      padding: '1.5rem', 
                      background: 'var(--color-surface-muted)', 
                      border: '2px dashed var(--color-border)', 
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: 'var(--color-text-muted)'
                    }}>
                      <p style={{ margin: '0 0 0.5rem 0' }}>No terms and conditions available.</p>
                      <p style={{ margin: 0, fontSize: '14px' }}>
                        Go to the Policy page to add your terms and conditions.
                      </p>
                    </div>
                  ) : (
                    <div className="quotations__policy-groups">
                      {Object.entries(groupedPolicies).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, policies]) => (
                        <div key={groupName} className="quotations__policy-group" style={{
                          marginBottom: '1rem',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          {/* Group Header */}
                          <div style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            padding: '0.6rem 0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer'
                          }}>
                            <label style={{
                              margin: 0,
                              color: '#ffffff',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.45rem',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}>
                              <input
                                type="checkbox"
                                className="quotations__group-checkbox"
                                checked={isGroupFullySelected(groupName)}
                                ref={el => {
                                  if (el) el.indeterminate = isGroupPartiallySelected(groupName);
                                }}
                                onChange={(e) => handleSelectGroup(groupName, e.target.checked)}
                                style={{ 
                                  cursor: 'pointer',
                                  accentColor: '#ffffff'
                                }}
                              />
                              <span>📋 {groupName}</span>
                            </label>
                            <span style={{
                              background: 'rgba(255, 255, 255, 0.2)',
                              padding: '0.2rem 0.6rem',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '500',
                              color: '#ffffff'
                            }}>
                              {policies.length} term{policies.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          
                          {/* Group Terms */}
                          <div style={{ 
                            padding: '0.6rem 0.75rem',
                            background: 'var(--color-surface)'
                          }}>
                            {policies.map((policy, idx) => (
                              <label
                                key={policy.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '0.45rem 0.55rem',
                                  marginBottom: idx === policies.length - 1 ? 0 : '0.35rem',
                                  cursor: 'pointer',
                                  borderRadius: '6px',
                                  transition: 'all 0.18s ease',
                                  background: (formData.terms || []).includes(policy.content) ? 'var(--color-success-soft)' : 'transparent',
                                  border: (formData.terms || []).includes(policy.content) ? '1px solid var(--color-success-border)' : '1px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                  if (!(formData.terms || []).includes(policy.content)) {
                                    e.currentTarget.style.background = 'var(--color-surface-hover)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!(formData.terms || []).includes(policy.content)) {
                                    e.currentTarget.style.background = 'transparent';
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  className="quotations__term-checkbox"
                                  checked={(formData.terms || []).includes(policy.content)}
                                  onChange={() => handleTermToggle(policy.content)}
                                  style={{ 
                                    marginTop: '0',
                                    marginRight: '0.6rem',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    accentColor: '#10b981',
                                    alignSelf: 'center'
                                  }}
                                />
                                <span style={{ 
                                  fontSize: '14.5px',
                                  color: 'var(--color-text)',
                                  lineHeight: '1.4',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {policy.content}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(formData.terms || []).filter(term => Object.values(groupedPolicies).flat().some(policy => policy.content === term)).length > 0 && (
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: 'var(--color-success-soft)',
                      border: '1px solid var(--color-success-border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: 'var(--color-success-dark)'
                    }}>
                      <strong>{formData.terms.filter(term => Object.values(groupedPolicies).flat().some(policy => policy.content === term)).length}</strong> term{formData.terms.filter(term => Object.values(groupedPolicies).flat().some(policy => policy.content === term)).length !== 1 ? 's' : ''} selected
                    </div>
                  )}
                  {/* signatures are printed only in the generated PDF */}
                </div>
              </div>

              {/* PDF Options */}
                <div className="quotations__toggle-options" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                  <label className="quotations__toggle-option quotations__toggle-option--bottom">
                    <span className="switch switch--small">
                      <input
                        type="checkbox"
                        checked={formData.showDescriptionInPdf === true}
                        onChange={(e) => setFormData({ ...formData, showDescriptionInPdf: e.target.checked })}
                      />
                      <span className="slider" />
                    </span>
                    <span>Show Description in PDF</span>
                  </label>
                  <label className="quotations__toggle-option quotations__toggle-option--bottom">
                    <span className="switch switch--small">
                      <input
                        type="checkbox"
                        checked={formData.showPaymentDetailsInPdf !== false}
                        onChange={(e) => setFormData({ ...formData, showPaymentDetailsInPdf: e.target.checked })}
                      />
                      <span className="slider" />
                    </span>
                    <span>Show Payment Details in PDF</span>
                  </label>
                  <label className="quotations__toggle-option quotations__toggle-option--bottom">
                    <span className="switch switch--small">
                      <input
                        type="checkbox"
                        checked={formData.showQrCodeInPdf !== false}
                        onChange={(e) => setFormData({ ...formData, showQrCodeInPdf: e.target.checked })}
                      />
                      <span className="slider" />
                    </span>
                    <span>Show QR Code in PDF</span>
                  </label>
                </div>
            </div>

            <div className="quotations__form-actions">
              {/* Show Save Draft only for CREATE (not when editing an existing quotation) */}
              {!editingQuotationId && (
                <button
                  className="btn-secondary"
                  onClick={() => {
                    try { saveQuotationDraftToSession(makeQuotationDraftKey()); } catch (e) {}
                    try { setShowCreateModal(false); } catch (e) {}
                    try { showToast('⚠️ your form saved temporarily, please complete and submit soon.'); } catch (e) {}
                  }}
                  type="button"
                >
                  Save Draft
                </button>
              )}
              {/* Reset moved to modal headerActions */}
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSubmitQuotation}
                disabled={!formData.clientName || !/^\d{10}$/.test(formData.clientPhone || '') || formData.items.every(item => !item.service)}
              >
                Create Quotation
              </button>
            </div>
        </div>
      </Modal>

      {/* View Quotation Modal (render children only when selectedQuotation exists) */}
      {showViewModal && selectedQuotation && (
        <Modal
          isOpen={true}
          contentClassName="quotations__modal"
          title={`Quotation ${selectedQuotation.id}`}
          headerActions={<button className="btn-secondary" onClick={() => handleEditFromView(selectedQuotation)} title="Edit quotation"><FaEdit /> Edit</button>}
          onClose={() => setShowViewModal(false)}
        >
          <div className="quotations__view">
            <div className="quotations__view-header">
              <div className="quotations__view-header-top">
                <div className="quotations__view-status-left">
                  <span
                    className={`quotations__status quotations__status--${selectedQuotation.status}`}
                    style={{ background: `${getStatusColor(selectedQuotation.status)}20`, color: getStatusColor(selectedQuotation.status) }}
                  >
                    {getStatusIcon(selectedQuotation.status)}
                    {selectedQuotation.status.charAt(0).toUpperCase() + selectedQuotation.status.slice(1)}
                  </span>
                </div>

                <div />

                <div className="quotations__view-top-actions" />
              </div>

              <div className="quotations__view-client-row">
                <div className="quotations__view-client">
                  <h3><Highlight text={selectedQuotation.clientName} query={searchQuery} /></h3>
                  <p><Highlight text={selectedQuotation.clientPhone} query={searchQuery} /></p>
                  <p><Highlight text={selectedQuotation.clientEmail} query={searchQuery} /></p>
                </div>

                <div className="quotations__view-date-right">
                  <label>{t('common.date')}</label>
                  <span><Highlight text={formatDateDisplay(selectedQuotation.date)} query={searchQuery} /></span>
                </div>
                <div className="quotations__view-date-right">
                  <label>{t('quotation.labelEventDate')}</label>
                  <span><Highlight text={selectedQuotation.mainEventDate ? formatDateDisplay(selectedQuotation.mainEventDate) : ''} query={searchQuery} /></span>
                </div>
              </div>
            </div>

            <div className="quotations__view-section">
              <h4>{t('common.services')}</h4>
              <div className="quotations__view-items">
                {(() => {
                  const filteredItems = Array.isArray(selectedQuotation.items) ? selectedQuotation.items.filter(it => it && it.service && it.service.toString().trim() !== '') : [];
                  if (filteredItems.length > 0) {
                    return (
                      <table className="quotations__items-table">
                        <thead>
                          <tr>
                            <th>{t('quotation.colService')}</th>
                            <th className="quotations__item-qty">{t('quotation.colQtyShort')}</th>
                            <th className="quotations__item-unit">{t('quotation.colUnitPrice')}</th>
                            <th className="quotations__item-line">{t('quotation.colLineTotal')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredItems.map((item, idx) => {
                            const qty = parseFloat(item.quantity) || 0;
                            const unit = parseFloat(item.price) || 0;
                            const line = qty * unit;
                            return (
                              <tr key={idx}>
                                <td><Highlight text={item.service || item.description || 'Service'} query={searchQuery} /></td>
                                <td className="quotations__item-qty">{qty}</td>
                                <td className="quotations__item-unit"><Highlight text={`₹${unit.toLocaleString('en-IN')}`} query={searchQuery} /></td>
                                <td className="quotations__item-line"><Highlight text={`₹${line.toLocaleString('en-IN')}`} query={searchQuery} /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  }
                  // fallback to simple services list if there are no detailed items
                  return (
                    <ul className="quotations__view-services">
                      {Array.isArray(selectedQuotation.services) && selectedQuotation.services.length > 0 ? (
                        selectedQuotation.services.map((service, idx) => (
                          <li key={idx}><Highlight text={service} query={searchQuery} /></li>
                        ))
                      ) : (
                        <li>{t('common.noServices')}</li>
                      )}
                    </ul>
                  );
                })()}
              </div>
            </div>

            {selectedQuotation.additionalCharges && selectedQuotation.additionalCharges.length > 0 && (
              <div className="quotations__view-section">
                <h4>{t('common.sectionCharges')}</h4>
                <ul className="quotations__view-charges">
                  {selectedQuotation.additionalCharges.map((c, i) => (
                    <li key={i}>
                      <span className="quotations__charge-desc"><Highlight text={c.description || 'Charge'} query={searchQuery} /></span>
                      <span className="quotations__charge-amount"><Highlight text={`₹${(parseFloat(c.amount) || 0).toLocaleString('en-IN')}`} query={searchQuery} /></span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="quotations__view-section quotations__view-section--total">
              <h4>{t('billing.colTotalAmt')}</h4>
              <div className="quotations__view-amount">
                <Highlight text={`₹${selectedQuotation.totalAmount.toLocaleString('en-IN')}`} query={searchQuery} />
              </div>
            </div>

            {/* notes removed per UI change */}

            {selectedQuotation.status === 'pending' && (
              <div className="quotations__view-actions">
                <button 
                  className="btn-success"
                  onClick={() => {
                    handleUpdateStatus(selectedQuotation.id, 'approved');
                    setShowViewModal(false);
                  }}
                >
                  <FaCheck /> {t('quotation.approveBtn')}
                </button>
                <button 
                  className="btn-danger"
                  onClick={() => {
                    handleUpdateStatus(selectedQuotation.id, 'rejected');
                    setShowViewModal(false);
                  }}
                >
                  <FaTimes /> {t('quotation.rejectBtn')}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
      {/* Share dialog: To Client vs To Others (WhatsApp / Email) */}
      <ShareChoiceModal
        isOpen={!!shareTarget}
        onClose={() => setShareTarget(null)}
        channel={shareChannel}
        title={shareChannel === 'whatsapp' ? 'Share Quotation via WhatsApp' : 'Share Quotation via Email'}
        docLabel={shareTarget ? `${shareTarget.id || ''}${shareTarget.clientName ? ` • ${shareTarget.clientName}` : ''}` : ''}
        clientName={shareTarget?.clientName || ''}
        clientContact={shareChannel === 'whatsapp' ? (shareTarget?.clientPhone || '') : (shareTarget?.clientEmail || '')}
        hasClientContact={shareChannel === 'whatsapp' ? !!normalizeSharePhone(shareTarget?.clientPhone) : !!((shareTarget?.clientEmail || '').trim())}
        clientMessage={shareTarget ? (shareChannel === 'whatsapp' ? buildQuotationWhatsApp({ quotation: shareTarget, company, audience: 'client' }) : buildQuotationEmail({ quotation: shareTarget, company, audience: 'client' }).body) : ''}
        othersMessage={shareTarget ? (shareChannel === 'whatsapp' ? buildQuotationWhatsApp({ quotation: shareTarget, company, audience: 'others' }) : buildQuotationEmail({ quotation: shareTarget, company, audience: 'others' }).body) : ''}
        clientSubject={shareTarget && shareChannel === 'email' ? buildQuotationEmail({ quotation: shareTarget, company, audience: 'client' }).subject : ''}
        othersSubject={shareTarget && shareChannel === 'email' ? buildQuotationEmail({ quotation: shareTarget, company, audience: 'others' }).subject : ''}
        onSendToClient={handleShareToClient}
        onShareOthersViaApp={handleShareOthersViaApp}
      />
    </div>
  );
};

export default Quotations;
